import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const Header = ({ activeSection, onOpenConsultation }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const handleBookNowClick = () => {
    setIsMobileMenuOpen(false);
    onOpenConsultation();
  };

  const navItems = [
    { id: 'home', label: 'HOME' },
    { id: 'about', label: 'ABOUT' },
    { id: 'services', label: 'SERVICES' },
    { id: 'contact', label: 'CONTACT' },
    { id: 'partnerships', label: 'PARTNERSHIPS' },
    { id: 'client-portal', label: 'CLIENT PORTAL', isExternal: true, href: '#' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/98 backdrop-blur-md shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <button
            onClick={() => scrollToSection('home')}
            className="flex items-center hover:opacity-80 transition-opacity duration-300"
          >
            <img 
              src="/images/heise-wellness-logo.png" 
              alt="Heise Wellness" 
              className="h-12 lg:h-14 w-auto"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navItems.map((item) => (
              item.isExternal ? (
                <a
                  key={item.id}
                  href={item.href}
                  className="text-sm font-medium tracking-wide text-gray-700 hover:text-purple-600 transition-colors duration-300"
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
                    activeSection === item.id
                      ? 'text-purple-600'
                      : 'text-gray-700 hover:text-purple-600'
                  }`}
                >
                  {item.label}
                </button>
              )
            ))}
            <button
              onClick={handleBookNowClick}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 text-sm font-medium uppercase tracking-wide transition-all duration-300 hover:shadow-lg ml-4 rounded-md cursor-pointer"
              data-testid="header-book-now-btn"
            >
              BOOK NOW
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-purple-600 transition-colors duration-300"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white/98 backdrop-blur-md border-t border-gray-200">
          <nav className="px-6 py-4 space-y-3">
            {navItems.map((item) => (
              item.isExternal ? (
                <a
                  key={item.id}
                  href={item.href}
                  className="block w-full text-left py-2 text-sm font-medium tracking-wide text-gray-700 hover:text-purple-600 transition-colors duration-300"
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${
                    activeSection === item.id
                      ? 'text-purple-600'
                      : 'text-gray-700 hover:text-purple-600'
                  }`}
                >
                  {item.label}
                </button>
              )
            ))}
            <button
              onClick={handleBookNowClick}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 text-sm font-medium uppercase tracking-wide rounded-md text-center cursor-pointer"
              data-testid="mobile-book-now-btn"
            >
              BOOK NOW
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
